import { FunctionComponent, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { FileDown, AlertCircle } from 'lucide-react'
import { toast } from 'react-toastify'
import TealToolTip from 'components/TealToolTip'
import ConfirmDialog from 'components/shared/ConfirmDialog'
import { requestWordDocument } from 'services/QAService'
import notificationToast from 'helpers/notificationToast'
import { setSignUpModalIsOpen } from 'redux/actions'
import selectIsLogged from 'redux/selectors/selectIsLogged'

type LoadingFor = 'with' | 'without' | null

interface WordDownloadButtonProps {
  selectedIds: string[]
}

const WordDownloadButton: FunctionComponent<WordDownloadButtonProps> = ({ selectedIds }) => {
  const [open, setOpen] = useState(false)
  const [loadingFor, setLoadingFor] = useState<LoadingFor>(null)
  const isLogged = useSelector(selectIsLogged)
  const dispatch = useDispatch()

  const handleDownload = (removeCrossRefs: boolean): void => {
    setLoadingFor(removeCrossRefs ? 'without' : 'with')
    requestWordDocument(selectedIds, removeCrossRefs).subscribe({
      next: () => {
        setLoadingFor(null)
        setOpen(false)
      },
      error: () => {
        setLoadingFor(null)
        notificationToast(<AlertCircle size={18} />, toast.TYPE.ERROR, 'Failed to download Word document. Please try again.')
      }
    })
  }

  const loading = loadingFor !== null

  const handleClick = (): void => {
    if (isLogged) {
      setOpen(true)
    } else {
      dispatch(setSignUpModalIsOpen(true))
    }
  }

  return (
    <div className='mh2 mh1-m'>
      <TealToolTip title='Download in Word' placement='top' arrow={false} bgColor='dark'>
        <button
          onClick={handleClick}
          data-event-category='Share'
          data-event-action='Word Download'
          className='bg-transparent bn pointer flex items-center justify-center black hover-bg-ghost-white br2 pa1'
        >
          <FileDown size={20} strokeWidth={1.5} />
        </button>
      </TealToolTip>
      <ConfirmDialog
        open={open}
        onClose={() => !loading && setOpen(false)}
        title='Download as Word Document'
        disabled={loading}
        cancelText={<>With cross-<br />references</>} // Force line break - makes layout more even imo
        cancelAttributes={{
          'data-event-category': 'Download',
          'data-event-action': 'Word-CrossRef',
          'data-event-label': `[${selectedIds.join(',')}]`
        }}
        onCancel={() => handleDownload(false)}
        cancelLoading={loadingFor === 'with'}
        confirmText='Without cross-references *'
        confirmAttributes={{
          'data-event-category': 'Download',
          'data-event-action': 'Word-No-CrossRef',
          'data-event-label': `[${selectedIds.join(',')}]`
        }}
        onConfirm={() => handleDownload(true)}
        confirmLoading={loadingFor === 'without'}
        footnote='* Removes cross-references to other FC Q&A from downloaded content. Generates in redline for ease of review, please check the redline before accepting. No AI is used in this process.'
      />
    </div>
  )
}

export default WordDownloadButton
